<template>
    <div>
        <span class="d-flex align-items-center">
            <i
                class="ni-2x mdi mdi-circle pr-1"
                :class="{
                    'mdi-image': file.type === 'image',
                    'mdi-file': file.type === 'pdf',
                    'mdi-play': file.type === 'video',
                }"
            >
            </i
            >{{ file.filename }}
        </span>
        <!-- Acesso anônimo via URL assinada: download direto, sem o fluxo
             autenticado de generate/status (backlog#410) -->
        <div v-if="publicFileUrl" style="width: fit-content">
            <base-button download @click="downloadPublicFile">
                <i class="fa fa-download"></i> Download
            </base-button>
        </div>
        <!-- Start Protected file -->
        <div
            v-else-if="
                file.config &&
                (file.download.status === 'none' ||
                    file.download.status === 'processing')
            "
        >
            <base-button
                download
                v-if="downloadUrl"
                @click="generateDownload(file)"
            >
                <i class="fa fa-download"></i> Download File
            </base-button>
            <base-button
                download
                v-else
                :disabled="file.download.status === 'processing'"
                @click="generateDownload(file)"
            >
                <i
                    :class="
                        file.download.status === 'processing'
                            ? 'fa fa-spinner fa-spin'
                            : 'fa fa-upload'
                    "
                ></i>
                Request file
            </base-button>
        </div>
        <!-- End Protected file -->
        <div v-else style="width: fit-content">
            <base-button
                class="mr-0"
                :class="[
                    {
                        'disable-border-radious-bottom':
                            progressDownload ||
                            file.download.status === 'downloading',
                    },
                ]"
                download
                :disabled="file.download.status === 'downloading'"
                :key="file.id"
                @click="downloadFile(file)"
            >
                <i
                    :class="
                        file.download.status === 'downloading'
                            ? 'fa fa-spinner fa-spin'
                            : 'fa fa-download'
                    "
                ></i>
                Download
            </base-button>
            <base-progress
                v-show="
                    progressDownload || file.download.status === 'downloading'
                "
                class="progress-bar-elevation"
                type="primary"
                :height="5"
                :value="progressDownload"
                progressClasses="disable-border-radious-top mb-0"
            ></base-progress>
        </div>
    </div>
</template>

<script>
import BaseButton from '../../../argon/components/BaseButton';
import BaseProgress from '../../../argon/components/BaseProgress';

export default {
    name: 'ContentDownload',
    props: {
        content: Object,
        file: Object,
    },
    components: {
        BaseButton,
        BaseProgress,
    },
    data() {
        return {
            downloadUrl: false,
            progressDownload: 0,
        };
    },
    computed: {
        language() {
            return this.$store.getters.LANGUAGE;
        },
        /**
         * URL assinada de download deste arquivo no acesso anônimo
         * (gravada pela Lesson.vue a partir da API signed-url — backlog#410).
         * Só existe em acesso público: a navegação autenticada remove a chave.
         */
        publicFileUrl() {
            const signedUrls = localStorage.getItem(
                `content_${this.content.id_lesson_content}_download_urls`,
            );
            if (!signedUrls) return null;
            try {
                const urls = JSON.parse(signedUrls);
                return (urls && urls.files && urls.files[this.file.id]) || null;
            } catch (e) {
                return null;
            }
        },
    },
    created() {
        this.registerBroadcast();
    },
    methods: {
        /** Baixa o arquivo pela URL assinada (disposition attachment). */
        downloadPublicFile() {
            window.location.href = this.publicFileUrl;
        },
        registerBroadcast() {
            // caso o download ja tenha sido requisitado e foi gerado nao há necessidade do websocket persistir em monitorar
            if (this.file.download.status === 'processing') {
                this.updateFileStatus();
            } else if (this.file.download.status === 'complete') {
                this.downloadUrl = this.getDownloadUrl();
            }
        },
        generateDownload(file) {
            window.axios
                .get(
                    `/app/files/generate/${this.content.id_lesson_content}/${file.secret}`,
                    {
                        params: {
                            watermark: 1,
                        },
                    },
                )
                .then((response) => {
                    this.file.download = response.data.data.download;
                    this.registerBroadcast();
                });
        },
        getDownloadUrl() {
            return `/app/files/download/${this.content.id_lesson_content}/${this.file.secret}`;
        },
        async updateFileStatus() {
            const downloadStatus = await window.axios.put(
                `/app/files/download_status/${this.file.download.id}`,
            );
            this.setStatus(downloadStatus.data.download_status);
        },
        async resetDownloadStatus(file) {
            if (file.download && file.download.id) {
                try {
                    await window.axios.put(`/app/files/download_status/${file.download.id}`, {
                        status: 'none'
                    });
                    this.setStatus('none');
                    this.downloadUrl = false;
                } catch (e) {
                    console.error('Error resetting download status:', e);
                }
            }
        },
        downloadFile(file) {
            this.setStatus('downloading');

            window
                .axios(
                    `/app/files/download/${this.content.id_lesson_content}/${file.secret}`,
                    {
                        responseType: 'blob', // important
                        onDownloadProgress: (progress) => {
                            const load =
                                progress.loaded > this.file.size
                                    ? this.file.size
                                    : progress.loaded;
                            this.progressDownload =
                                (load * 100) / this.file.size;
                        },
                    },
                )
                .then((response) => {
                    console.log(response.data);
                    const url = window.URL.createObjectURL(
                        new Blob([response.data]),
                    );
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', file.filename);
                    document.body.appendChild(link);
                    link.click();

                    this.setStatus('complete');
                })
                .catch((error) => {
                    this.setStatus('complete');
                    this.progressDownload = 0;

                    // Verifica se há resposta HTTP
                    if (error.response) {
                        const status = error.response.status;

                        // se for status 401
                        // não tem mais permissão para baixar o arquivo
                        if (status === 401) {
                            this.$notify({
                                message: this.$t('content.uploads.rules_failed'),
                                type: 'warning',
                            });
                        }

                        // se for status 410
                        // arquivo expirou no servidor, precisa clicar novamente
                        else if (status === 410) {
                            this.resetDownloadStatus(file);
                            this.$notify({
                                message: 'Arquivo expirou. Clique novamente para gerar um novo download.',
                                type: 'info',
                            });
                        }

                        // se for status 404
                        // arquivo não encontrado
                        else if (status === 404) {
                            this.resetDownloadStatus(file);
                            this.$notify({
                                message: 'Arquivo não encontrado. Clique novamente para gerar um novo download.',
                                type: 'warning',
                            });
                        }

                        // outros erros do servidor
                        else if (status >= 500) {
                            this.$notify({
                                message: 'Erro no servidor ao processar o arquivo. Tente novamente em alguns instantes.',
                                type: 'error',
                            });
                        }

                        // outros erros
                        else {
                            this.$notify({
                                message: error.response.data?.message || 'Erro ao baixar arquivo. Tente novamente.',
                                type: 'error',
                            });
                        }
                    } else {
                        // Erro de rede ou timeout
                        this.$notify({
                            message: 'Erro de conexão. Verifique sua internet e tente novamente.',
                            type: 'error',
                        });
                    }
                });
        },
        setStatus(status) {
            this.$store.commit('SET_FILE_DOWNLOAD_STATUS', {
                id: this.file.id,
                status,
            });
        },
    },
};
</script>

<style scoped>
.progress-bar-elevation {
    transform: translateY(-1px);
}
.disable-border-radious-bottom {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}
</style>
